import type {
  AttendanceRowResult,
  AttendanceStatus,
  AudienceEntry,
  AudienceView,
  CreateEventRequest,
  CreateEventSeriesRequest,
  EventParticipantView,
  EventSeriesView,
  EventStatus,
  EventType,
  RescheduleEventSeriesRequest,
  ResourceKind,
  RsvpResponse,
  SupportedLocale,
  SurfaceType,
} from '@stm/contracts';
import { isCalendarDate } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { athleteDisplayName, formatCalendarDate } from './athletes';

export const eventTypeKeys: Readonly<Record<EventType, TranslationKey>> = {
  on_ice_training: 'calendar.type.on_ice_training',
  off_ice_training: 'calendar.type.off_ice_training',
  match: 'calendar.type.match',
  assessment: 'calendar.type.assessment',
  meeting: 'calendar.type.meeting',
};

export const eventStatusKeys: Readonly<Record<EventStatus, TranslationKey>> = {
  draft: 'calendar.status.draft',
  published: 'calendar.status.published',
  cancelled: 'calendar.status.cancelled',
  completed: 'calendar.status.completed',
};

export const rsvpKeys: Readonly<Record<RsvpResponse, TranslationKey>> = {
  yes: 'calendar.rsvp.yes',
  no: 'calendar.rsvp.no',
  tentative: 'calendar.rsvp.tentative',
};

export const attendanceKeys: Readonly<Record<AttendanceStatus, TranslationKey>> = {
  present: 'calendar.attendance.present',
  late: 'calendar.attendance.late',
  absent_excused: 'calendar.attendance.absent_excused',
  absent_unexcused: 'calendar.attendance.absent_unexcused',
  not_recorded: 'calendar.attendance.not_recorded',
};

export const weekdayKeys: Readonly<Record<number, TranslationKey>> = {
  1: 'calendar.weekday.1',
  2: 'calendar.weekday.2',
  3: 'calendar.weekday.3',
  4: 'calendar.weekday.4',
  5: 'calendar.weekday.5',
  6: 'calendar.weekday.6',
  7: 'calendar.weekday.7',
};

export const resourceKindKeys: Readonly<Record<ResourceKind, TranslationKey>> = {
  rink: 'calendar.resourceKind.rink',
  zone: 'calendar.resourceKind.zone',
  room: 'calendar.resourceKind.room',
  field: 'calendar.resourceKind.field',
};

export const surfaceKeys: Readonly<Record<SurfaceType, TranslationKey>> = {
  ice: 'calendar.surface.ice',
  dryland: 'calendar.surface.dryland',
  other: 'calendar.surface.other',
};

/** Local calendar day of an instant in the browser's zone, `YYYY-MM-DD`. */
export function localDayOf(instant: Date): string {
  const year = String(instant.getFullYear()).padStart(4, '0');
  const month = String(instant.getMonth() + 1).padStart(2, '0');
  const day = String(instant.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addCalendarDays(isoDate: string, days: number): string {
  const moved = new Date(`${isoDate}T00:00:00.000Z`);
  moved.setUTCDate(moved.getUTCDate() + days);
  return moved.toISOString().slice(0, 10);
}

export interface EventQuery {
  fromDate: string;
  toDate: string;
  departmentId?: string;
  status?: string;
  cursor?: string | null;
}

export function eventsPath(base: string, query: EventQuery): string {
  const parameters = new URLSearchParams({ fromDate: query.fromDate, toDate: query.toDate });
  if (query.departmentId !== undefined && query.departmentId !== '') {
    parameters.set('departmentId', query.departmentId);
  }
  if (query.status !== undefined && query.status !== '') parameters.set('status', query.status);
  if (query.cursor !== undefined && query.cursor !== null) parameters.set('cursor', query.cursor);
  return `${base}/events?${parameters.toString()}`;
}

export function myEventsPath(base: string, fromDate: string, toDate: string): string {
  return `${base}/my-events?${new URLSearchParams({ fromDate, toDate }).toString()}`;
}

/** "Mon, 12 Oct 2026 · 18:00–19:30": the wall-clock fields are in the event's own zone. */
export function formatEventWhen(
  event: { date: string; startTime: string; endTime: string },
  locale: SupportedLocale,
): string {
  return `${formatCalendarDate(event.date, locale)} · ${event.startTime}–${event.endTime}`;
}

/** A rate between 0 and 1 as a whole percentage; no recorded outcome shows a dash. */
export function percentLabel(rate: number | null): string {
  return rate === null ? '—' : `${String(Math.round(rate * 100))}%`;
}

export function groupByDate<Item extends { date: string }>(
  items: readonly Item[],
): { date: string; items: Item[] }[] {
  const groups: { date: string; items: Item[] }[] = [];
  for (const item of items) {
    const last = groups.at(-1);
    if (last?.date === item.date) last.items.push(item);
    else groups.push({ date: item.date, items: [item] });
  }
  return groups;
}

/** Who an event is for, as the audience picker holds it. */
export interface AudienceSelection {
  teamIds: string[];
  groupIds: string[];
  athleteIds: string[];
}

export interface EventForm extends AudienceSelection {
  departmentId: string;
  eventType: EventType;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  resourceId: string;
  capacity: string;
  arrivalMinutes: string;
  /** Hours before the start until which guardians may answer. */
  rsvpHours: string;
  leadSelf: boolean;
  repeat: boolean;
  weekdays: number[];
  /** Every how many weeks the series repeats (1 = every week). */
  intervalWeeks: number;
  until: string;
  publishAll: boolean;
}

export function emptyEventForm(date: string, departmentId: string): EventForm {
  return {
    departmentId,
    eventType: 'on_ice_training',
    title: '',
    date,
    startTime: '18:00',
    endTime: '19:00',
    location: '',
    resourceId: '',
    capacity: '',
    arrivalMinutes: '',
    rsvpHours: '2',
    teamIds: [],
    groupIds: [],
    athleteIds: [],
    leadSelf: false,
    repeat: false,
    weekdays: [],
    intervalWeeks: 1,
    until: '',
    publishAll: false,
  };
}

const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;

/** A whole number from a text box, or null when empty or not a non-negative integer. */
export function wholeNumber(text: string): number | null {
  const trimmed = text.trim();
  return /^\d{1,6}$/.test(trimmed) ? Number(trimmed) : null;
}

export function canSubmitEvent(form: EventForm): boolean {
  if (form.departmentId === '' || form.title.trim() === '') return false;
  if (!isCalendarDate(form.date) || !timePattern.test(form.startTime)) return false;
  if (!timePattern.test(form.endTime)) return false;
  if (form.capacity.trim() !== '' && wholeNumber(form.capacity) === null) return false;
  if (form.arrivalMinutes.trim() !== '' && wholeNumber(form.arrivalMinutes) === null) return false;
  if (wholeNumber(form.rsvpHours) === null) return false;
  if (form.repeat) {
    if (form.weekdays.length === 0) return false;
    if (form.until !== '' && (!isCalendarDate(form.until) || form.until < form.date)) return false;
    // A weekly series cannot end before it starts on the same clock.
    return form.endTime !== form.startTime;
  }
  return form.endTime > form.startTime;
}

export function audienceOf(selection: AudienceSelection): AudienceEntry[] {
  return [
    ...selection.teamIds.map((teamSeasonId): AudienceEntry => ({ kind: 'team', teamSeasonId })),
    ...selection.groupIds.map((trainingGroupId): AudienceEntry => ({
      kind: 'group',
      trainingGroupId,
    })),
    ...selection.athleteIds.map((athleteId): AudienceEntry => ({ kind: 'athlete', athleteId })),
  ];
}

/** The picker state of an existing event's audience, with the names of the athletes named in it. */
export function selectionOfAudience(audience: readonly AudienceView[]): {
  selection: AudienceSelection;
  names: Record<string, string>;
} {
  const selection: AudienceSelection = { teamIds: [], groupIds: [], athleteIds: [] };
  const names: Record<string, string> = {};
  for (const entry of audience) {
    if (entry.kind === 'team') selection.teamIds.push(entry.teamSeasonId);
    else if (entry.kind === 'group') selection.groupIds.push(entry.trainingGroupId);
    else {
      selection.athleteIds.push(entry.athleteId);
      names[entry.athleteId] = entry.name;
    }
  }
  return { selection, names };
}

/** True when both hold the same teams, groups and athletes, in any order. */
export function sameSelection(first: AudienceSelection, second: AudienceSelection): boolean {
  const same = (left: readonly string[], right: readonly string[]): boolean =>
    left.length === right.length && left.every((identifier) => right.includes(identifier));
  return (
    same(first.teamIds, second.teamIds) &&
    same(first.groupIds, second.groupIds) &&
    same(first.athleteIds, second.athleteIds)
  );
}

export function isEmptySelection(selection: AudienceSelection): boolean {
  return (
    selection.teamIds.length === 0 &&
    selection.groupIds.length === 0 &&
    selection.athleteIds.length === 0
  );
}

/** Toggles one value in a list without mutating it. */
export function toggled<Value>(values: readonly Value[], value: Value): Value[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

type SharedFields = Pick<
  CreateEventRequest,
  | 'departmentId'
  | 'eventType'
  | 'title'
  | 'location'
  | 'resourceId'
  | 'capacity'
  | 'arrivalMinutesBefore'
  | 'rsvpDeadlineMinutes'
  | 'audience'
  | 'staffOrganizationUserIds'
>;

function sharedFields(form: EventForm, ownMembershipId: string | null): SharedFields {
  const capacity = wholeNumber(form.capacity);
  const arrival = wholeNumber(form.arrivalMinutes);
  return {
    departmentId: form.departmentId,
    eventType: form.eventType,
    title: form.title.trim(),
    location: form.location.trim() === '' ? null : form.location.trim(),
    resourceId: form.resourceId === '' ? null : form.resourceId,
    capacity,
    arrivalMinutesBefore: arrival,
    rsvpDeadlineMinutes: (wholeNumber(form.rsvpHours) ?? 2) * 60,
    audience: audienceOf(form),
    staffOrganizationUserIds: form.leadSelf && ownMembershipId !== null ? [ownMembershipId] : [],
  };
}

export function eventRequestBody(
  form: EventForm,
  ownMembershipId: string | null,
): CreateEventRequest {
  return {
    ...sharedFields(form, ownMembershipId),
    date: form.date,
    startTime: form.startTime,
    endTime: form.endTime,
  };
}

/** Minutes from start to end on a clock that may cross midnight. */
export function durationMinutes(startTime: string, endTime: string): number {
  const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
  };
  return (toMinutes(endTime) - toMinutes(startTime) + 1440) % 1440;
}

export function seriesRequestBody(
  form: EventForm,
  ownMembershipId: string | null,
): CreateEventSeriesRequest {
  return {
    ...sharedFields(form, ownMembershipId),
    startsOn: form.date,
    startTime: form.startTime,
    durationMinutes: durationMinutes(form.startTime, form.endTime),
    intervalWeeks: form.intervalWeeks,
    weekdays: [...form.weekdays].sort((first, second) => first - second),
    untilOn: form.until === '' ? null : form.until,
    occurrenceCount: null,
    publishOccurrences: form.publishAll,
    horizonDays: 90,
  };
}

/** How often a series can repeat in the forms: every week up to every fourth week. */
export const intervalWeekChoices = [1, 2, 3, 4] as const;

/** "Every week on Mon, Wed · 18:00–19:30" in the person's language. */
export function describeSeries(
  series: Pick<EventSeriesView, 'intervalWeeks' | 'weekdays' | 'startTime' | 'durationMinutes'>,
  locale: SupportedLocale,
): string {
  const days = [...series.weekdays]
    .sort((first, second) => first - second)
    .map((weekday) => translate(locale, weekdayKeys[weekday] ?? 'calendar.weekday.1'))
    .join(', ');
  const frequency =
    series.intervalWeeks === 1
      ? translate(locale, 'calendar.series.everyWeek').replace('{days}', days)
      : translate(locale, 'calendar.series.everyNthWeek')
          .replace('{weeks}', String(series.intervalWeeks))
          .replace('{days}', days);
  return `${frequency} · ${series.startTime}–${endTimeOf(series.startTime, series.durationMinutes)}`;
}

/** The local clock time a span of minutes after `startTime` ends, wrapping past midnight. */
export function endTimeOf(startTime: string, minutes: number): string {
  const [hours, startMinutes] = startTime.split(':').map(Number);
  const total = ((hours ?? 0) * 60 + (startMinutes ?? 0) + minutes) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export interface RescheduleForm {
  fromDate: string;
  weekdays: number[];
  intervalWeeks: number;
  startTime: string;
  endTime: string;
  until: string;
  reason: string;
  notifyParticipants: boolean;
}

export function rescheduleFormOf(
  series: EventSeriesView,
  fromDate: string,
  reason: string,
): RescheduleForm {
  return {
    fromDate,
    weekdays: [...series.weekdays],
    intervalWeeks: series.intervalWeeks,
    startTime: series.startTime,
    endTime: endTimeOf(series.startTime, series.durationMinutes),
    until: series.untilOn ?? '',
    reason,
    notifyParticipants: true,
  };
}

export function canSubmitReschedule(form: RescheduleForm, today: string): boolean {
  if (!isCalendarDate(form.fromDate) || form.fromDate < today) return false;
  if (form.weekdays.length === 0 || form.reason.trim() === '') return false;
  if (!timePattern.test(form.startTime) || !timePattern.test(form.endTime)) return false;
  if (form.startTime === form.endTime) return false;
  return form.until === '' || (isCalendarDate(form.until) && form.until >= form.fromDate);
}

export function rescheduleRequestBody(form: RescheduleForm): RescheduleEventSeriesRequest {
  return {
    fromDate: form.fromDate,
    weekdays: [...form.weekdays].sort((first, second) => first - second),
    intervalWeeks: form.intervalWeeks,
    startTime: form.startTime,
    durationMinutes: durationMinutes(form.startTime, form.endTime),
    untilOn: form.until === '' ? null : form.until,
    reason: form.reason.trim(),
    notifyParticipants: form.notifyParticipants,
  };
}

/** Occurrences that can still be ticked on or off: not started yet and not completed. */
export function isTogglableOccurrence(
  event: { status: EventStatus; startsAt: string },
  now: Date,
): boolean {
  return event.status !== 'completed' && new Date(event.startsAt).getTime() > now.getTime();
}

export interface AttendanceRow {
  participantId: string;
  athleteId: string;
  name: string;
  rsvp: RsvpResponse | null;
  /** Stored status and the row version it was read with. */
  status: AttendanceStatus;
  version: number;
  /** The coach's unsaved choice, if different from the stored status. */
  pending: AttendanceStatus | null;
  /** Another coach changed this row while this sheet was open. */
  conflict: boolean;
}

export function sheetRows(
  participants: readonly EventParticipantView[],
  locale: SupportedLocale,
): AttendanceRow[] {
  return participants.map((participant) => ({
    participantId: participant.participantId,
    athleteId: participant.athleteId,
    name: athleteDisplayName(participant, locale),
    rsvp: participant.rsvp?.response ?? null,
    status: participant.attendance.status,
    version: participant.attendance.version,
    pending: null,
    conflict: false,
  }));
}

/** The status a row shows: the unsaved choice if there is one. */
export function shownStatus(row: AttendanceRow): AttendanceStatus {
  return row.pending ?? row.status;
}

/** Choosing the stored status again clears the pending change. */
export function chooseStatus(
  rows: readonly AttendanceRow[],
  participantId: string,
  status: AttendanceStatus,
): AttendanceRow[] {
  return rows.map((row) =>
    row.participantId === participantId
      ? { ...row, pending: status === row.status ? null : status, conflict: false }
      : row,
  );
}

/** Marks everyone whose attendance is still open as present, keeping choices already made. */
export function markOpenRowsPresent(rows: readonly AttendanceRow[]): AttendanceRow[] {
  return rows.map((row) =>
    row.status === 'not_recorded' && row.pending === null ? { ...row, pending: 'present' } : row,
  );
}

export function pendingCount(rows: readonly AttendanceRow[]): number {
  return rows.filter((row) => row.pending !== null).length;
}

/** What to send: only rows changed on this sheet, each with the version the coach saw. */
export function changedItems(
  rows: readonly AttendanceRow[],
): { participantId: string; status: AttendanceStatus; version: number }[] {
  return rows.flatMap((row) =>
    row.pending === null
      ? []
      : [{ participantId: row.participantId, status: row.pending, version: row.version }],
  );
}

/**
 * Folds the server's answer into the sheet. Saved rows take their new version; a conflicting row
 * takes the other coach's stored status and version, is flagged, and loses the unsaved choice so
 * nothing is overwritten without the coach seeing it first.
 */
export function applyBatchResults(
  rows: readonly AttendanceRow[],
  results: readonly AttendanceRowResult[],
): AttendanceRow[] {
  const byParticipant = new Map(results.map((result) => [result.participantId, result]));
  return rows.flatMap((row): AttendanceRow[] => {
    const result = byParticipant.get(row.participantId);
    if (result === undefined) return [row];
    if (result.outcome === 'not_found') return [];
    if (result.status === null || result.version === null) return [row];
    return [
      {
        ...row,
        status: result.status,
        version: result.version,
        pending: null,
        conflict: result.outcome === 'conflict',
      },
    ];
  });
}

export function answerCounts(
  participants: readonly EventParticipantView[],
): Record<RsvpResponse | 'none', number> {
  const counts = { yes: 0, no: 0, tentative: 0, none: 0 };
  for (const participant of participants) {
    counts[participant.rsvp?.response ?? 'none'] += 1;
  }
  return counts;
}
